import { createColumnHelper } from '@tanstack/react-table';
import { TextKey } from '@mews-commander/enums';
import { lt } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { LocalizedDateFromUtc, SpacedDash } from '@mews-commander/ui';
import { PolicyException } from '../../../../../types';
import { CancellationPolicyName, SettlementRuleName } from '../../../../../../../components';
import { PolicyExceptionsTableRowAction } from './PolicyExceptionsTableRowAction';
import { PolicyExceptionStatusCell } from './PolicyExceptionStatusCell';
import { PolicyExceptionRatesCell } from './PolicyExceptionRatesCell';

const columnHelper = createColumnHelper<PolicyException>();

export const getPolicyExceptionsColumns = (isRequirePaymentDuringOciEnabled: boolean) => [
    columnHelper.accessor('timeUnitIntervals', {
        id: 'status',
        header: () => lt('Status'),
        cell: (props) => <PolicyExceptionStatusCell timeUnitIntervals={props.getValue()} />,
    }),
    columnHelper.accessor('timeUnitIntervals', {
        header: () => lt(TextKey.DatePlural),
        cell: (props) => (
            <Stack vertical>
                {props.getValue().map(({ firstTimeUnitStartUtc, lastTimeUnitStartUtc }) => (
                    <Typography
                        key={`${firstTimeUnitStartUtc}-${lastTimeUnitStartUtc}`}
                        textStyle="bodyMedium"
                        whiteSpace="nowrap"
                        as="div"
                    >
                        <LocalizedDateFromUtc utcString={firstTimeUnitStartUtc} />
                        {SpacedDash}
                        <LocalizedDateFromUtc utcString={lastTimeUnitStartUtc} />
                    </Typography>
                ))}
            </Stack>
        ),
    }),
    columnHelper.accessor('rateIds', {
        header: () => lt(TextKey.RatePlural),
        cell: (props) => <PolicyExceptionRatesCell rateIds={props.getValue()} />,
    }),
    columnHelper.accessor('settlementRules', {
        header: () => lt(TextKey.Payment),
        cell: (props) => (
            <Stack vertical>
                {props.getValue().map((settlementRule) => (
                    <SettlementRuleName {...settlementRule} key={settlementRule.id} format="nameAndParameters" />
                ))}
            </Stack>
        ),
    }),
    columnHelper.accessor('cancellationPolicies', {
        header: () => lt(TextKey.Cancellation),
        cell: (props) => {
            const cancellationPolicies = props.getValue();
            return cancellationPolicies ? (
                <Stack vertical>
                    {cancellationPolicies.map((cancellationPolicy) => (
                        <CancellationPolicyName
                            {...cancellationPolicy}
                            key={cancellationPolicy.id}
                            format="nameAndParameters"
                        />
                    ))}
                </Stack>
            ) : null;
        },
    }),
    isRequirePaymentDuringOciEnabled
        ? columnHelper.accessor('guestExperienceOptions', {
              header: () => lt(TextKey.CheckInRequirePaymentDetails),
              cell: (props) => {
                  const value = props.getValue();
                  return value.length === 0 ? (
                      lt('No')
                  ) : (
                      <Stack vertical>
                          {value.map((options) => (
                              <Typography textStyle="bodyMedium" key={options} as="span">
                                  {lt('Yes')}
                              </Typography>
                          ))}
                      </Stack>
                  );
              },
          })
        : null,
    columnHelper.display({
        id: 'actions',
        meta: { align: 'end' },
        cell: (props) => <PolicyExceptionsTableRowAction policyException={props.row.original} />,
    }),
];
